import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLang } from '../contexts/LanguageContext';
import { Cookie, Shield } from 'lucide-react';

const COOKIE_KEY = 'zimbra_cookie_consent';

const texts = {
  de: {
    title: 'Cookie-Einstellungen',
    body: 'Wir verwenden technisch notwendige Cookies sowie optionale Analyse-Cookies (Google Analytics, Matomo), um unsere Website zu verbessern. Analyse-Cookies werden nur mit Ihrer Zustimmung gesetzt.',
    necessary: 'Notwendige Cookies sind f\u00fcr den Betrieb der Website erforderlich und k\u00f6nnen nicht deaktiviert werden.',
    accept_all: 'Alle akzeptieren',
    accept_necessary: 'Nur notwendige',
    privacy: 'Datenschutzerkl\u00e4rung',
  },
  en: {
    title: 'Cookie Settings',
    body: 'We use technically necessary cookies and optional analytics cookies (Google Analytics, Matomo) to improve our website. Analytics cookies are only set with your consent.',
    necessary: 'Necessary cookies are required for the website to function and cannot be disabled.',
    accept_all: 'Accept all',
    accept_necessary: 'Necessary only',
    privacy: 'Privacy Policy',
  },
};

function loadGoogleAnalytics() {
  if (document.getElementById('ga-script')) return;
  const script = document.createElement('script');
  script.id = 'ga-script';
  script.async = true;
  script.src = 'https://www.googletagmanager.com/gtag/js?id=G-B6JS2HPS3X';
  document.head.appendChild(script);

  script.onload = () => {
    window.dataLayer = window.dataLayer || [];
    function gtag() { window.dataLayer.push(arguments); }
    window.gtag = gtag;
    gtag('js', new Date());
    gtag('config', 'G-B6JS2HPS3X', {
      anonymize_ip: true,
      cookie_flags: 'SameSite=None;Secure',
    });
  };
}

function loadMatomo() {
  if (document.getElementById('matomo-script')) return;
  const script = document.createElement('script');
  script.id = 'matomo-script';
  script.type = 'text/javascript';
  script.text = `
    var _paq = window._paq = window._paq || [];
    _paq.push(['setDocumentTitle', document.domain + '/' + document.title]);
    _paq.push(['setCookieDomain', '*.zimbra.jetzt']);
    _paq.push(['setDomains', ['*.zimbra.jetzt']]);
    _paq.push(['enableCrossDomainLinking']);
    _paq.push(['trackPageView']);
    _paq.push(['enableLinkTracking']);
    _paq.push(['enableHeartBeatTimer', 15]);
    (function() {
      var u='https://stats.host-on.de/';
      _paq.push(['setTrackerUrl', u+'matomo.php']);
      _paq.push(['setSiteId', '11']);
      var d=document, g=d.createElement('script'), s=d.getElementsByTagName('script')[0];
      g.async=true; g.src=u+'matomo.js'; s.parentNode.insertBefore(g,s);
    })();
  `;
  document.head.appendChild(script);
}

function initAnalytics() {
  loadGoogleAnalytics();
  loadMatomo();
}

export function getConsentLevel() {
  return localStorage.getItem(COOKIE_KEY);
}

export default function CookieBanner() {
  const { lang } = useLang();
  const t = texts[lang] || texts.de;
  const [visible, setVisible] = useState(false);

  const checkAndLoadAnalytics = useCallback(() => {
    const consent = localStorage.getItem(COOKIE_KEY);
    if (consent === 'all') {
      initAnalytics();
    }
  }, []);

  useEffect(() => {
    const consent = localStorage.getItem(COOKIE_KEY);
    if (!consent) {
      const timer = setTimeout(() => setVisible(true), 1500);
      return () => clearTimeout(timer);
    }
    if (consent === 'all') {
      initAnalytics();
    }
  }, []);

  const accept = (level) => {
    localStorage.setItem(COOKIE_KEY, level);
    setVisible(false);
    if (level === 'all') {
      initAnalytics();
    }
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ duration: 0.4, ease: 'easeOut' }}
          className="fixed bottom-0 left-0 right-0 z-[60] p-3 sm:p-4 lg:p-6"
          data-testid="cookie-banner"
        >
          <div className="max-w-4xl mx-auto bg-surface/95 backdrop-blur-2xl border border-white/[0.08] rounded-2xl p-4 sm:p-6 lg:p-8 shadow-[0_-10px_60px_rgba(0,0,0,0.5)]">
            <div className="flex flex-col sm:flex-row items-start gap-3 sm:gap-4">
              <div className="hidden sm:flex w-10 h-10 rounded-xl bg-neon/10 items-center justify-center flex-shrink-0 mt-0.5">
                <Cookie className="w-5 h-5 text-neon" />
              </div>
              <div className="flex-1 w-full">
                <h3 className="font-heading text-sm sm:text-base font-bold text-white mb-1.5 sm:mb-2">{t.title}</h3>
                <p className="text-xs sm:text-sm text-zinc-400 font-light leading-relaxed mb-1">{t.body}</p>
                <p className="text-[10px] sm:text-xs text-zinc-600 mb-4 sm:mb-5 flex items-center gap-1.5">
                  <Shield className="w-3 h-3 flex-shrink-0" />
                  {t.necessary}
                </p>
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3">
                  <button
                    onClick={() => accept('all')}
                    className="inline-flex items-center justify-center bg-neon text-black font-bold text-xs sm:text-sm uppercase tracking-wide rounded-full px-5 sm:px-6 py-2.5 sm:py-3 hover:bg-white hover:shadow-[0_0_20px_rgba(212,255,0,0.5)] transition-all duration-300"
                    data-testid="cookie-accept-all"
                  >
                    {t.accept_all}
                  </button>
                  <button
                    onClick={() => accept('necessary')}
                    className="inline-flex items-center justify-center bg-white/[0.05] text-zinc-300 font-semibold text-xs sm:text-sm rounded-full px-5 sm:px-6 py-2.5 sm:py-3 border border-white/10 hover:bg-white/[0.08] hover:text-white transition-all duration-300"
                    data-testid="cookie-accept-necessary"
                  >
                    {t.accept_necessary}
                  </button>
                  <a
                    href="/datenschutz"
                    className="text-xs text-zinc-600 hover:text-zinc-400 transition-colors underline underline-offset-2 sm:ml-2"
                    data-testid="cookie-privacy-link"
                  >
                    {t.privacy}
                  </a>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
